import styled from '@emotion/styled'
import { ExtranetShopProductDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetExpandSelectChevron,
  ExtranetTableColumn,
  ExtranetTableGroupColumn,
  ExtranetTableRow,
  ExtranetTextDiv,
  MediaQuery,
  formatDateString,
  htmlStringSanitizer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacBadge,
  PharmacButton,
  PharmacCarousel,
  PharmacCarouselItem,
  PharmacH4,
  PharmacH5,
  PharmacIcon,
  PharmacImage,
  PharmacPortalTooltip,
  PharmacText,
  PharmacTooltip,
} from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import { useShoppingCartContext } from '../navigation/shopping-cart/ShoppingCartContext'
import { NoResultsResponse } from '../responses/NoResultsResponse'
import { useShop } from './useShop'
import { formatPriceDecimal } from './util/shop-utils'

const InfoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: var(--spacing-small);

  p {
    ${MediaQuery.maxTablet} {
      margin-bottom: var(--spacing-tiny);
    }
  }
`

const Tag = styled(PharmacBadge)`
  ${MediaQuery.maxTablet} {
    margin-bottom: var(--spacing-tiny);
  }
`

const Image = styled(PharmacImage)`
  max-width: 100% !important;
  max-height: 440px !important;
`

export const ShopProductVideo = ({ videoProduct }: { videoProduct: ExtranetShopProductDto }) => {
  const { t, i18n } = useLanguage()
  const { minDesktop, maxMobile, minTablet } = useBreakpoint()
  const { customer } = useCustomerContext()
  const {
    hasBuyerRole,
    subscribeToFreeProduct,
    accessPeriodDuration,
    formatTimecode,
    myRoles,
    hasBuyingBan,
    formatTimecodeToFancyForm,
    hasRequiredRoles,
  } = useShop(videoProduct.productId, videoProduct)
  const { isPharmacCustomerService, user } = useAuthenticationContext()

  const hook = useShoppingCartContext()

  const { data: video } = useQuery({
    queryKey: ['__getProductVideo', customer?.id, videoProduct.productId],
    queryFn: async () =>
      await api.shop.getProductVideo(Number(customer?.id), videoProduct.productId),
    enabled: !!customer!.id && !!videoProduct.productId,
  })

  if (!videoProduct) return null

  return (
    <ExtranetContainer
      flexColumn
      paddingSmallHorizontal
      paddingLargeBottom={!minDesktop}
      full
    >
      <ExtranetContainer
        full
        paddingSmallVertical={minDesktop}
        paddingMediumTop={!minDesktop}
      >
        <InfoContainer>
          {videoProduct.language && (
            <PharmacText
              marginLargeRight={minDesktop}
              marginMediumRight={!minDesktop}
            >
              {t(`common:${videoProduct.language?.code as 'fi' | 'sv' | 'en'}`)}
            </PharmacText>
          )}
          <PharmacText
            marginLargeRight={minDesktop}
            marginMediumRight={!minDesktop}
          >
            {t('extranet:shopProducts.video')}
          </PharmacText>
          <PharmacText
            marginLargeRight={minDesktop}
            marginMediumRight={!minDesktop}
          >
            {t('extranet:shop.published')}:{' '}
            {videoProduct?.publishDate && formatDateString(videoProduct?.publishDate)}
          </PharmacText>
          <PharmacText marginLargeRight>{videoProduct?.manufacturer?.name}</PharmacText>
          {minDesktop &&
            videoProduct?.tags?.map((a: any) => (
              <Tag
                key={a.id}
                variant="primary"
                marginSmallRight
                small
              >
                {a.name}
              </Tag>
            ))}
        </InfoContainer>
      </ExtranetContainer>
      {!minDesktop && (
        <ExtranetContainer
          full
          paddingSmallVertical
          flexWrap
        >
          {videoProduct?.tags?.map((a: any) => (
            <Tag
              key={a.id}
              variant="primary"
              marginSmallRight
              small
            >
              {a.name}
            </Tag>
          ))}
        </ExtranetContainer>
      )}
      <ExtranetContainer
        marginSmallTop
        full
        flexColumn
        paddingMediumBottom={!minDesktop}
      >
        <PharmacH4 marginMediumBottom>{videoProduct?.title}</PharmacH4>
      </ExtranetContainer>

      <ExtranetContainer
        flexColumn
        full
        paddingSmall
        style={{
          backgroundColor: 'var(--color-accent-2)',
          borderRadius: '15px',
          border: '1.5px solid var(--color-accent-4)',
        }}
      >
        <ExtranetContainer
          full
          flexRow={minDesktop}
          flexColumn={!minDesktop}
        >
          <ExtranetContainer
            twoThirds={minDesktop}
            full={!minDesktop}
            marginSmallRight
          >
            {videoProduct.isSubscribed &&
              hasRequiredRoles(videoProduct.professionalRoles, user?.professionalRoles) && (
                <>
                  {video ? (
                    <iframe
                      width="100%"
                      height={minTablet ? '450px' : '100%'}
                      src={video?.videoUrl}
                      allowFullScreen
                      style={{ border: 'none', backgroundColor: 'black' }}
                    />
                  ) : (
                    <>
                      {videoProduct?.images?.length !== 0 ? (
                        <PharmacCarousel>
                          {(videoProduct?.images || []).map((a) => (
                            <PharmacCarouselItem key={a.id}>
                              <Image
                                imageUrl={
                                  minDesktop
                                    ? a.desktopUrl
                                    : maxMobile
                                      ? a.mobileUrl
                                      : a.desktopWideUrl
                                }
                              />
                            </PharmacCarouselItem>
                          ))}
                        </PharmacCarousel>
                      ) : (
                        <NoResultsResponse
                          icon="videoOff"
                          title={t('extranet:shop.product.videoNotAvailable')}
                          description=" "
                        />
                      )}
                    </>
                  )}
                </>
              )}
            {videoProduct.isSubscribed &&
              !hasRequiredRoles(videoProduct.professionalRoles, user?.professionalRoles) && (
                <NoResultsResponse
                  icon="videoOff"
                  title={t('extranet:shop.product.videoNotAvailable')}
                  description={t('extranet:shop.noRequiredProfRolesForDownloadTooltip')}
                />
              )}
            {!videoProduct.isSubscribed && (
              <>
                {videoProduct.videoTeaserUrl ? (
                  <iframe
                    width="100%"
                    height={minTablet ? '450px' : '100%'}
                    src={videoProduct.videoTeaserUrl}
                    allowFullScreen
                    style={{ border: 'none', backgroundColor: 'black' }}
                  />
                ) : (
                  <>
                    {videoProduct?.images?.length !== 0 ? (
                      <PharmacCarousel>
                        {(videoProduct?.images || []).map((a) => (
                          <PharmacCarouselItem key={a.id}>
                            <Image
                              imageUrl={
                                minDesktop
                                  ? a.desktopUrl
                                  : maxMobile
                                    ? a.mobileUrl
                                    : a.desktopWideUrl
                              }
                            />
                          </PharmacCarouselItem>
                        ))}
                      </PharmacCarousel>
                    ) : (
                      <NoResultsResponse
                        icon="videoOff"
                        title={t('extranet:shop.product.videoNotAvailable')}
                        description=" "
                      />
                    )}
                  </>
                )}
              </>
            )}
          </ExtranetContainer>
          {minDesktop ? (
            <ExtranetContainer
              third
              flexColumn
            >
              {!videoProduct.isSubscribed && videoProduct.videoTeaserUrl !== null && (
                <ExtranetContainer
                  full
                  paddingSmallVertical
                  marginMediumBottom
                  paddingMediumLeft
                  spaceBetween
                  flexWrap
                  style={{
                    borderRadius: '15px',
                    border: '1.5px solid var(--color-accent-4)',
                    backgroundColor: 'var(--color-white)',
                  }}
                >
                  <PharmacH5 color="purple">{t('extranet:shop.product.preview')}</PharmacH5>
                  <ExtranetContainer
                    flexRow
                    alignCenter
                    paddingMediumRight
                  >
                    <PharmacIcon
                      icon="playCircle"
                      stroke="var(--color-primary)"
                    />
                    <PharmacText paddingSmallLeft>
                      {videoProduct.videoTeaserDuration &&
                        formatTimecodeToFancyForm(videoProduct.videoTeaserDuration)}
                    </PharmacText>
                  </ExtranetContainer>
                </ExtranetContainer>
              )}
              <ExtranetContainer
                marginMediumTop={!minTablet}
                flexColumn
                full
                style={{
                  borderRadius: '15px',
                  border: '1.5px solid var(--color-accent-4)',
                  height: videoProduct.isSubscribed
                    ? '450px'
                    : !videoProduct.isSubscribed && videoProduct.videoTeaserUrl
                      ? '390px'
                      : '320px',
                  backgroundColor: 'var(--color-white)',
                  opacity: videoProduct.isSubscribed ? undefined : '.6',
                }}
              >
                <ExtranetContainer
                  full
                  paddingMedium
                  style={{
                    backgroundColor: videoProduct.isSubscribed
                      ? 'var(--color-purple)'
                      : 'var(--color-accent-2)',
                    borderBottom: '1.5px solid var(--color-accent-4)',
                    borderRadius: '15px 15px 0 0',
                  }}
                >
                  <PharmacH5 color={videoProduct.isSubscribed ? 'white' : undefined}>
                    {t('extranet:shopProducts.chapters')}
                  </PharmacH5>
                </ExtranetContainer>
                <ExtranetContainer
                  flexColumn
                  paddingMedium
                  full
                  style={{ overflow: 'auto' }}
                >
                  {videoProduct?.videoChapters?.map((chapter) => (
                    <ExtranetContainer
                      flexRow
                      full
                      paddingSmallBottom
                    >
                      <PharmacText
                        color="purple"
                        weight="semibold"
                        paddingMediumRight
                        style={{ minWidth: '80px' }}
                      >
                        {formatTimecode(Number(chapter.timecode))}
                      </PharmacText>
                      <PharmacText>{chapter.title}</PharmacText>
                    </ExtranetContainer>
                  ))}
                </ExtranetContainer>
              </ExtranetContainer>
            </ExtranetContainer>
          ) : (
            <ExtranetContainer
              flexColumn
              full
            >
              {!videoProduct.isSubscribed && videoProduct.videoTeaserUrl !== null && (
                <ExtranetContainer
                  full
                  paddingSmallVertical
                  marginSmallVertical
                  paddingMediumLeft
                  spaceBetween
                  style={{
                    borderRadius: '15px',
                    border: '1.5px solid var(--color-accent-4)',
                    backgroundColor: 'var(--color-white)',
                  }}
                >
                  <PharmacH5 color="purple">{t('extranet:shop.product.preview')}</PharmacH5>
                  <ExtranetContainer
                    flexRow
                    alignCenter
                    paddingMediumRight
                  >
                    <PharmacIcon
                      icon="playCircle"
                      stroke="var(--color-primary)"
                    />
                    <PharmacText paddingSmallLeft>
                      {videoProduct.videoTeaserDuration &&
                        formatTimecodeToFancyForm(videoProduct.videoTeaserDuration)}
                    </PharmacText>
                  </ExtranetContainer>
                </ExtranetContainer>
              )}

              <ExtranetContainer
                full
                marginMediumTop={videoProduct.isSubscribed || videoProduct.videoTeaserUrl === null}
              >
                <ExtranetTableRow
                  style={{
                    padding: 'var(--spacing-small)',
                    background: videoProduct.isSubscribed
                      ? 'var(--color-purple)'
                      : 'var(--color-accent-2)',
                    borderRadius: '15px',
                    opacity: videoProduct.isSubscribed ? undefined : '.6',
                  }}
                  expand={
                    <ExtranetContainer
                      flexColumn
                      paddingMedium
                      full
                      style={{ background: 'var(--color-white)' }}
                    >
                      {videoProduct?.videoChapters.map((chapters) => (
                        <ExtranetContainer
                          flexRow
                          full
                          paddingSmallBottom
                        >
                          <PharmacText
                            color="purple"
                            weight="semibold"
                            marginMediumRight
                            style={{ minWidth: '65px' }}
                          >
                            {formatTimecode(Number(chapters.timecode))}
                          </PharmacText>
                          <PharmacText
                            paddingSmallLeft
                            paddingSmallRight
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {chapters.title}
                          </PharmacText>
                        </ExtranetContainer>
                      ))}
                    </ExtranetContainer>
                  }
                  toggleExpand={() => null}
                  children={({ isExpanded }) => (
                    <ExtranetTableGroupColumn
                      style={{
                        minHeight: 'var(--input-medium-height)',
                      }}
                    >
                      <ExtranetTableRow>
                        <ExtranetTableColumn
                          style={{
                            minHeight: 'var(--input-medium-height)',
                          }}
                        >
                          <PharmacH5 color={videoProduct.isSubscribed ? 'white' : undefined}>
                            {t('extranet:shopProducts.chapters')}
                          </PharmacH5>
                        </ExtranetTableColumn>
                        <ExtranetContainer>
                          <ExtranetExpandSelectChevron isExpanded={isExpanded} />
                        </ExtranetContainer>
                      </ExtranetTableRow>
                    </ExtranetTableGroupColumn>
                  )}
                />
              </ExtranetContainer>
            </ExtranetContainer>
          )}
        </ExtranetContainer>

        <ExtranetContainer
          flexRow={minTablet}
          flexColumn={!minTablet}
          full
        >
          <ExtranetContainer
            third={minTablet}
            full={!minTablet}
            paddingLargeVertical
            paddingMediumHorizontal
            flexColumn
            alignCenter={maxMobile}
          >
            <ExtranetContainer
              flexRow
              alignCenter
              paddingLargeBottom
            >
              {!videoProduct.isSubscribed && (
                <>
                  <PharmacIcon
                    icon="alertCircle"
                    style={{ color: 'var(--color-warning)' }}
                  />
                  <PharmacText paddingSmallLeft>
                    {t('extranet:shop.product.notActivated')}
                  </PharmacText>
                </>
              )}
              {videoProduct.isSubscribed && (
                <>
                  <PharmacIcon
                    icon="check"
                    style={{ color: 'var(--color-success)' }}
                  />
                  <PharmacText paddingSmallLeft>{t('extranet:shop.product.activated')}</PharmacText>
                </>
              )}
            </ExtranetContainer>
            <ExtranetContainer
              flexRow
              justifyCenter
              alignCenter
              paddingLargeBottom
            >
              <PharmacIcon
                icon="clock"
                style={{ color: 'var(--color-primary)' }}
              />
              <PharmacText paddingSmallHorizontal>
                {videoProduct.videoDuration &&
                  formatTimecodeToFancyForm(videoProduct.videoDuration)}
              </PharmacText>
            </ExtranetContainer>
            <ExtranetContainer
              flexRow
              alignCenter
              paddingLargeBottom
            >
              <>
                <PharmacIcon
                  icon="flag"
                  style={{ color: 'var(--color-primary)' }}
                />
                {(!videoProduct.isSubscribed && videoProduct.accessPeriod?.isUnlimited) ||
                (videoProduct.isSubscribed && !videoProduct.subscriptionEndDate) ? (
                  <PharmacText paddingSmallLeft>
                    {t('extranet:shop.product.unlimitedAccess')}
                  </PharmacText>
                ) : (
                  <>
                    <PharmacText paddingSmallLeft>
                      {t('extranet:shop.product.viewable')}
                      {videoProduct.isSubscribed
                        ? ` ${i18n.language !== 'fi' ? t('extranet:shop.product.till') : ''} ${videoProduct.subscriptionEndDate ? formatDateString(videoProduct.subscriptionEndDate) : '-'} ${i18n.language === 'fi' ? t('extranet:shop.product.till') : ''}`
                        : ':'}
                    </PharmacText>
                    {!videoProduct.isSubscribed && (
                      <>
                        <PharmacText paddingSmallLeft>
                          {videoProduct.accessPeriod?.duration}
                        </PharmacText>
                        {accessPeriodDuration(videoProduct.accessPeriod?.durationUnit)}
                      </>
                    )}
                  </>
                )}
              </>
            </ExtranetContainer>

            <ExtranetContainer
              flexRow
              justifyCenter
              alignCenter
              paddingLargeBottom
            >
              <ExtranetContainer
                alignCenter
                justifyCenter
                style={{
                  backgroundColor: '#B87EBC80',
                  height: '100%',
                  width: 'fit-content',
                  borderRadius: '25px',
                }}
              >
                <PharmacIcon
                  style={{ margin: 'var(--spacing-tiny)' }}
                  size="15px"
                  icon="euro"
                />
              </ExtranetContainer>
              {videoProduct.unitSellingPrice !== 0 ? (
                <>
                  <PharmacText
                    paddingSmallHorizontal
                    weight="bold"
                  >
                    {formatPriceDecimal(videoProduct.unitSellingPrice, 2)}
                  </PharmacText>
                  <PharmacText>
                    + {t('extranet:physicalProducts.vatLowercase')}{' '}
                    {formatPriceDecimal(Number(videoProduct.taxRate?.percentage))}%
                  </PharmacText>
                </>
              ) : (
                <PharmacText
                  paddingSmallHorizontal
                  weight="bold"
                >
                  {t('common:free')}
                </PharmacText>
              )}
            </ExtranetContainer>

            <ExtranetContainer
              paddingLargeBottom
              flexColumn
              full={minTablet}
            >
              {!videoProduct.isSubscribed && (
                <ExtranetContainer
                  flexRow
                  alignCenter
                  full
                >
                  {videoProduct.unitSellingPrice !== 0 ||
                  videoProduct.productFamily === 'service-product' ||
                  videoProduct.productFamily === 'physical-product' ? (
                    <>
                      <PharmacButton
                        fullWidth={minDesktop}
                        onClick={() =>
                          hook.addAndRemoveCartButton(videoProduct, videoProduct.productId)
                        }
                        disabled={
                          (!myRoles?.isAdmin &&
                            !hasBuyerRole &&
                            videoProduct.unitSellingPrice !== 0) ||
                          (hasBuyingBan && videoProduct.unitSellingPrice !== 0) ||
                          isPharmacCustomerService
                        }
                        variant={
                          (!myRoles?.isAdmin &&
                            !hasBuyerRole &&
                            videoProduct.unitSellingPrice !== 0) ||
                          (hasBuyingBan && videoProduct.unitSellingPrice !== 0) ||
                          isPharmacCustomerService
                            ? 'secondary'
                            : 'primary'
                        }
                      >
                        <PharmacIcon
                          size="20px"
                          icon="shoppingCart"
                        />
                        <PharmacText paddingSmallLeft>
                          {hook.selectedProducts[videoProduct.productId]?.includes(
                            videoProduct.productId
                          ) ? (
                            <>{t('extranet:shop.buttons.removeFromCart')}</>
                          ) : (
                            <>{t('extranet:shop.buttons.addToCart')}</>
                          )}
                        </PharmacText>
                      </PharmacButton>
                      {!myRoles?.isAdmin && !hasBuyerRole && !hasBuyingBan && (
                        <PharmacPortalTooltip
                          placement="top"
                          paddingSmallLeft
                          backgroundColor="var(--color-white)"
                          handle={
                            <PharmacIcon
                              style={{ color: 'var(--color-info)' }}
                              size="20px"
                              icon="info"
                            />
                          }
                        >
                          <PharmacText>{t('extranet:shop.noBuyerRoleTooltip')}</PharmacText>
                        </PharmacPortalTooltip>
                      )}
                      {hasBuyingBan && videoProduct.unitSellingPrice !== 0 && (
                        <PharmacTooltip
                          paddingSmallLeft
                          backgroundColor="var(--color-white)"
                          handle={
                            <PharmacIcon
                              style={{ color: 'var(--color-info)' }}
                              size="20px"
                              icon="info"
                            />
                          }
                        >
                          <PharmacText>{t('extranet:shop.purchaseRightDeniedTooltip')}</PharmacText>
                        </PharmacTooltip>
                      )}
                    </>
                  ) : (
                    <>
                      <PharmacButton
                        fullWidth={minDesktop}
                        onClick={() => subscribeToFreeProduct()}
                        disabled={!videoProduct.canBeFreeSubscribed || isPharmacCustomerService}
                        variant={
                          !videoProduct.canBeFreeSubscribed || isPharmacCustomerService
                            ? 'secondary'
                            : 'primary'
                        }
                      >
                        <PharmacIcon
                          size="20px"
                          icon="plus"
                        />
                        <PharmacText>{t('extranet:shop.buttons.addToOwnProducts')}</PharmacText>
                      </PharmacButton>
                      {!videoProduct.canBeFreeSubscribed && (
                        <PharmacPortalTooltip
                          placement="top"
                          paddingSmallLeft
                          backgroundColor="var(--color-white)"
                          handle={
                            <PharmacIcon
                              style={{ color: 'var(--color-info)' }}
                              size="20px"
                              icon="info"
                            />
                          }
                        >
                          <PharmacText>{t('extranet:shop.noRequiredProfRolesTooltip')}</PharmacText>
                        </PharmacPortalTooltip>
                      )}
                    </>
                  )}
                </ExtranetContainer>
              )}
              {videoProduct.isSubscribed && videoProduct.unitSellingPrice !== 0 && (
                <ExtranetContainer
                  full
                  alignCenter
                >
                  <PharmacButton
                    fullWidth
                    onClick={() =>
                      hook.addAndRemoveCartButton(videoProduct, videoProduct.productId)
                    }
                    variant={
                      (!myRoles?.isAdmin && !hasBuyerRole && videoProduct.unitSellingPrice !== 0) ||
                      (hasBuyingBan && videoProduct.unitSellingPrice !== 0) ||
                      isPharmacCustomerService
                        ? 'secondary'
                        : 'primary'
                    }
                    disabled={
                      (!myRoles?.isAdmin && !hasBuyerRole && videoProduct.unitSellingPrice !== 0) ||
                      (hasBuyingBan && videoProduct.unitSellingPrice !== 0) ||
                      isPharmacCustomerService
                    }
                  >
                    <PharmacIcon
                      size="20px"
                      icon="shoppingCart"
                    />

                    <PharmacText paddingSmallHorizontal>
                      {hook.selectedProducts &&
                      hook.selectedProducts[videoProduct.productId]?.includes(
                        videoProduct.productId
                      ) ? (
                        <>{t('extranet:shop.buttons.removeFromCart')}</>
                      ) : (
                        <> {t('extranet:shop.buttons.buyAgain')}</>
                      )}
                    </PharmacText>
                  </PharmacButton>
                  {hasBuyingBan && videoProduct.unitSellingPrice !== 0 && (
                    <PharmacTooltip
                      paddingSmallLeft
                      backgroundColor="var(--color-white)"
                      handle={
                        <PharmacIcon
                          style={{ color: 'var(--color-info)' }}
                          size="20px"
                          icon="info"
                        />
                      }
                    >
                      <PharmacText>{t('extranet:shop.purchaseRightDeniedTooltip')}</PharmacText>
                    </PharmacTooltip>
                  )}
                  {!myRoles?.isAdmin &&
                    !hasBuyerRole &&
                    !hasBuyingBan &&
                    videoProduct.unitSellingPrice !== 0 && (
                      <PharmacPortalTooltip
                        placement="top"
                        paddingSmallLeft
                        backgroundColor="var(--color-white)"
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="20px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>{t('extranet:shop.noBuyerRoleTooltip')}</PharmacText>
                      </PharmacPortalTooltip>
                    )}
                </ExtranetContainer>
              )}
            </ExtranetContainer>

            <ExtranetContainer
              full
              style={{ border: '2px solid var(--color-accent-4)', borderRadius: '8px' }}
            >
              <ExtranetContainer
                paddingSmall
                flexColumn
              >
                <PharmacText
                  weight="bold"
                  color="purple"
                  paddingMediumBottom
                >
                  {t('extranet:shop.product.requiredRoles')}
                </PharmacText>
                {videoProduct?.professionalRoles.length !== 0 ? (
                  videoProduct?.professionalRoles.map((role) => (
                    <PharmacText
                      key={role.id}
                      paddingSmallBottom
                    >
                      {role.displayName}
                    </PharmacText>
                  ))
                ) : (
                  <PharmacText paddingSmallBottom>
                    {t('extranet:shop.product.noRequiredRoles')}
                  </PharmacText>
                )}
              </ExtranetContainer>
            </ExtranetContainer>
          </ExtranetContainer>
          <div
            style={
              minTablet
                ? {
                    borderLeft: '2px solid var(--color-accent-4)',
                    height: '-webkit-fill-available',
                    marginTop: 'var(--spacing-large)',
                    marginBottom: 'var(--spacing-large)',
                  }
                : undefined
            }
          />
          <ExtranetContainer
            flexColumn
            full
            marginLargeTop={minTablet}
            marginLargeBottom
          >
            <ExtranetContainer
              flexColumn
              paddingLargeHorizontal={minTablet}
              paddingMedium={!minTablet}
              full
            >
              <ExtranetTextDiv
                data={videoProduct?.content}
                dangerouslySetInnerHTML={{
                  __html: htmlStringSanitizer(videoProduct?.content),
                }}
              />
            </ExtranetContainer>
          </ExtranetContainer>
        </ExtranetContainer>
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
